import React, { ReactNode, useMemo } from 'react';

import {
  AnnotateDropdown,
  ExportButton,
  ImportButton,
  ManagedDropdown,
  NoteButton,
  ScanDropdown,
  SeverityChangeDropdown,
  ShareButton,
  ShareDropdown,
  StatusChangeDropdown,
} from '@/components/actions';
import { getActionOrder } from '@/components/actions/config/actionOrder';
import { Row } from '@/components/Row';
import { cn } from '@/utils/classname';

import {
  AnnotateActionConfig,
  CustomActionConfig,
  ExportActionConfig,
  GenericCustomActionConfig,
  ImportActionConfig,
  ManageActionConfig,
  NoteActionConfig,
  ScanActionConfig,
  SeverityChangeActionConfig,
  ShareActionConfig,
  StatusChangeActionConfig,
  TableActionsBarProps,
} from './types';

/**
 * TableActionsBar - Shared action bar component for all table sections
 *
 * Provides a consistent action bar UI with configurable actions.
 * Automatically handles action ordering, disabled states, and layout.
 *
 * Features:
 * - Configuration-based action system
 * - Type-safe action builders
 * - Automatic left/right alignment via getActionOrder()
 * - Generic support for any item type
 *
 * @template T - Type of selected items
 *
 * @example
 * ```tsx
 * <TableActionsBar
 *   selectedItems={selectedAssets}
 *   actions={[
 *     buildAnnotateAction({ ... }),
 *     buildScanActionOptions({ scanOptions: [...] }),
 *   ]}
 *   disabled={selectedAssets.length === 0}
 *   testIdPrefix="asset"
 * />
 * ```
 */
export function TableActionsBar<T = unknown>({
  selectedItems: _selectedItems,
  actions,
  disabled = false,
  className,
  hideLabel,
  hideActions,
  testIdPrefix = 'table',
}: TableActionsBarProps<T>) {
  /**
   * Build action components from configurations
   *
   * Memoized to prevent unnecessary re-renders when props don't change.
   */
  const actionComponents = useMemo<Partial<Record<string, ReactNode>>>(() => {
    const components: Partial<Record<string, ReactNode>> = {};

    actions.forEach((action, index) => {
      if (action.hidden) return;

      const actionDisabled = disabled || hideActions || action.disabled;
      const testId =
        action.testId ||
        `${testIdPrefix}-${action.type.toLowerCase()}-dropdown`;

      switch (action.type) {
        case 'Annotate': {
          const config = action as AnnotateActionConfig;
          components.Annotate = (
            <AnnotateDropdown
              onCommentClick={config.handlers.onCommentClick}
              onTagClick={config.handlers.onTagClick}
              onRemoveTagClick={config.handlers.onRemoveTagClick}
              labels={config.labels}
              scanProfile={config.scanProfile}
              disabled={actionDisabled}
              data-testid={testId}
            />
          );
          break;
        }

        case 'Note': {
          const config = action as NoteActionConfig;
          components.Note = (
            <NoteButton
              onClick={config.onNoteClick}
              label={config.label}
              disabled={actionDisabled}
              data-testid={testId}
            />
          );
          break;
        }

        case 'Scan': {
          const config = action as ScanActionConfig;
          if (config.variant === 'options' && config.scanOptions) {
            components.Scan = (
              <ScanDropdown
                scanOptions={config.scanOptions}
                disabled={actionDisabled}
                data-testid={testId}
              />
            );
          } else if (config.variant === 'handlers' && config.handlers) {
            components.Scan = (
              <ScanDropdown
                onComprehensiveScan={config.handlers.onComprehensiveScan}
                onAffiliationScan={config.handlers.onAffiliationScan}
                onNetworkScan={config.handlers.onNetworkScan}
                onPortScan={config.handlers.onPortScan}
                onCrawlScan={config.handlers.onCrawlScan}
                onWebScan={config.handlers.onWebScan}
                onRescanClick={config.handlers.onRescanClick}
                onAffiliationClick={config.handlers.onAffiliationClick}
                disabledActions={config.disabledActions}
                hideActions={config.hideActions}
                labels={config.labels}
                disabled={actionDisabled}
                data-testid={testId}
              />
            );
          }
          break;
        }

        case 'Share': {
          const config = action as ShareActionConfig;
          if (
            config.variant === 'dropdown' &&
            config.onShareClick &&
            config.onExportClick
          ) {
            components.Share = (
              <ShareDropdown
                onExportClick={config.onExportClick}
                onShareClick={config.onShareClick}
                labels={config.labels}
                disabled={actionDisabled}
                data-testid={testId}
              />
            );
          } else if (config.variant === 'button-only') {
            components.Share = (
              <ShareButton
                onClick={
                  config.onShareClick || config.onExportClick || (() => {})
                }
                disabled={actionDisabled}
                data-testid={testId}
              />
            );
          }
          break;
        }

        case 'Export': {
          const config = action as ExportActionConfig;
          // Export should respect alwaysEnabled flag - if true, ignore parent disabled state
          const exportDisabled = config.alwaysEnabled
            ? (config.disabled ?? false) // Only use action's own disabled state
            : actionDisabled; // Use combined disabled state

          components.Export = (
            <ExportButton
              onClick={config.onExportClick}
              disabled={exportDisabled}
              data-testid={testId}
            />
          );
          break;
        }

        case 'Import': {
          const config = action as ImportActionConfig;
          // Import should respect alwaysEnabled flag - if true, ignore parent disabled state
          const importDisabled = config.alwaysEnabled
            ? (config.disabled ?? false) // Only use action's own disabled state
            : actionDisabled; // Use combined disabled state

          components.Import = (
            <ImportButton
              onClick={config.onImportClick}
              disabled={importDisabled}
              data-testid={testId}
            />
          );
          break;
        }

        case 'Manage': {
          // Check if it has a custom render function (for sections with custom Manage UI)
          if ('render' in action && typeof action.render === 'function') {
            components.Manage = (
              <div data-testid={testId}>
                {(action as { render: () => ReactNode }).render()}
              </div>
            );
          } else {
            const config = action as ManageActionConfig;
            components.Manage = (
              <ManagedDropdown
                onFreezeClick={config.handlers.onFreezeClick}
                onActivateClick={config.handlers.onActivateClick}
                onDeleteClick={config.handlers.onDeleteClick}
                onStatusChangeClick={config.handlers.onStatusChangeClick}
                statusOptions={config.statusOptions}
                onProfileChangeClick={config.handlers.onProfileChangeClick}
                profileOptions={config.profileOptions}
                currentProfileId={config.currentProfileId}
                labels={config.labels}
                shortcuts={config.shortcuts}
                hideActions={config.hideActions}
                disabled={actionDisabled}
                data-testid={testId}
              />
            );
          }
          break;
        }

        case 'Change Status': {
          // Check if it's the new standard action config
          if ('handlers' in action && 'risks' in action) {
            const config = action as StatusChangeActionConfig;
            components['Change Status'] = (
              <StatusChangeDropdown
                risks={config.risks}
                onUpdateRisk={config.handlers.onUpdateRisk}
                getRiskStatusLabel={config.getRiskStatusLabel}
                disabled={actionDisabled}
                hideLabel={config.hideLabel}
                data-testid={testId}
              />
            );
          } else {
            // Legacy custom action fallback
            const config = action as CustomActionConfig;
            components['Change Status'] = config.render();
          }
          break;
        }

        case 'Change Severity': {
          // Check if it's the new standard action config
          if (
            'handlers' in action &&
            'risks' in action &&
            'severityOptions' in action
          ) {
            const config = action as SeverityChangeActionConfig;
            components['Change Severity'] = (
              <SeverityChangeDropdown
                risks={config.risks}
                severityOptions={config.severityOptions}
                onSeverityChange={config.handlers.onSeverityChange}
                disabled={actionDisabled}
                hideLabel={config.hideLabel}
                data-testid={testId}
              />
            );
          } else {
            // Legacy custom action fallback
            const config = action as CustomActionConfig;
            components['Change Severity'] = config.render();
          }
          break;
        }

        case 'Custom': {
          const config = action as GenericCustomActionConfig;
          // Use unique key with index to support multiple custom actions
          const customKey = `Custom_${index}`;
          components[customKey] = (
            <div data-testid={config.testId}>{config.render()}</div>
          );
          break;
        }
      }
    });

    return components;
  }, [actions, disabled, hideActions, testIdPrefix]);

  /**
   * Get ordered action lists (left, right, and custom)
   *
   * Uses getActionOrder to automatically split actions into left (selection-dependent)
   * and right (always-enabled) sections. Custom actions are tracked separately with their indices,
   * split into "first" (render before left actions) and "default" (render after left actions).
   */
  const {
    left: leftOrder,
    right: rightOrder,
    firstCustomKeys,
    defaultCustomKeys,
  } = useMemo(() => {
    const standardActionNames = actions
      .filter(action => !action.hidden && action.type !== 'Custom')
      .map(action => action.type);
    const { left, right } = getActionOrder(standardActionNames);

    // Collect custom action keys by position
    const firstCustoms: string[] = [];
    const defaultCustoms: string[] = [];
    actions.forEach((action, index) => {
      if (!action.hidden && action.type === 'Custom') {
        const customAction = action as GenericCustomActionConfig;
        if (customAction.position === 'first') {
          firstCustoms.push(`Custom_${index}`);
        } else {
          defaultCustoms.push(`Custom_${index}`);
        }
      }
    });

    return {
      left,
      right,
      firstCustomKeys: firstCustoms,
      defaultCustomKeys: defaultCustoms,
    };
  }, [actions]);

  return (
    <Row className={cn('flex-wrap pb-0', className)}>
      {/* MERGED: All actions in single wrapping container */}
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-3',
          hideActions && 'invisible'
        )}
      >
        {/* Actions label - prevent shrinking */}
        {!hideLabel && <h2 className="shrink-0 text-sm font-bold">Actions:</h2>}

        {/* First-position custom actions - rendered before left actions */}
        {firstCustomKeys.map(customKey => (
          <div key={customKey} className="shrink-0">
            {actionComponents[customKey]}
          </div>
        ))}

        {/* Left actions (selection-dependent) - prevent shrinking */}
        {leftOrder.map(actionName => (
          <div key={actionName} className="shrink-0">
            {actionComponents[actionName]}
          </div>
        ))}

        {/* Default custom actions - rendered after left actions, in original order */}
        {defaultCustomKeys.map(customKey => (
          <div key={customKey} className="shrink-0">
            {actionComponents[customKey]}
          </div>
        ))}

        {/* Spacer to push right actions to end on wide screens */}
        {rightOrder.length > 0 && <div className="flex-grow" />}

        {/* Right actions (always enabled) - prevent shrinking, wraps to new line on narrow screens */}
        {rightOrder.map(actionName => (
          <div key={actionName} className="shrink-0">
            {actionComponents[actionName]}
          </div>
        ))}
      </div>
    </Row>
  );
}
